import { createVerifier, SigningAlgorithmListSlim } from "@ndn/keychain";
import { KeyLocator } from "@ndn/packet";
import { getOrInsert } from "@ndn/util";
import { CertSources } from "./cert-source/mod_browser.js";
/** Policy based verifier. */
export class PolicyVerifier {
    certSources;
    algoList;
    constructor(opts) {
        this.certSources = new CertSources(opts);
        this.algoList = opts.algoList ?? SigningAlgorithmListSlim;
    }
    /** Verify a packet. */
    async verify(pkt, now = Date.now()) {
        let lastPkt = pkt;
        const chain = [];
        let hasTrustAnchor = false;
        while (!hasTrustAnchor) {
            const klName = KeyLocator.mustGetName(lastPkt.sigInfo?.keyLocator);
            const ctx = this.checkKeyLocatorPolicy(lastPkt, klName);
            let hasCert = false;
            const certErrors = [];
            for await (const cert of this.certSources.findCerts(klName)) {
                try {
                    cert.checkValidity(now);
                    this.checkCertPolicy(lastPkt, cert, ctx);
                }
                catch (err) {
                    certErrors.push(`${cert.name}:${"\n\t"}${err}`);
                    continue;
                }
                chain.push(cert);
                lastPkt = cert.data;
                hasTrustAnchor = this.certSources.isTrustAnchor(cert);
                hasCert = true;
                break;
            }
            if (!hasCert) {
                if (certErrors.length === 0) {
                    throw new Error(`cannot retrieve certificate for ${klName}`);
                }
                else {
                    throw new Error(`no acceptable certificate for ${klName}${"\n\n"}${certErrors.join("\n")}`);
                }
            }
        }
        await Promise.all(chain.map(async (cert, i) => {
            if (i === 0) {
                return this.cryptoVerifyUncached(cert, pkt);
            }
            return this.cryptoVerifyCached(cert, chain[i - 1].data);
        }));
    }
    async cryptoVerifyUncached(cert, packet) {
        const key = await createVerifier(cert, { algoList: this.algoList, checkValidity: false });
        return key.verify(packet);
    }
    cryptoVerifyCache = new WeakMap();
    async cryptoVerifyCached(cert, packet) {
        const cacheForCert = getOrInsert(this.cryptoVerifyCache, cert, () => new WeakMap());
        return getOrInsert(cacheForCert, packet, () => this.cryptoVerifyUncached(cert, packet));
    }
}
